import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, RefreshCw, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/app/back-button';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { DataTable } from '@/components/ui/data-table';
import { useProcedureTemplate } from '@/lib/hooks/use-procedure-template';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { createInvitationsColumns } from './_partials/tables/InvitationsColumns';
import SheetInvitationCreate from './_partials/sheets/SheetInvitationCreate';
import AlertDialogRevokeInvitation from './_partials/alert-dialogs/AlertDialogRevokeInvitation';
import AlertDialogDeleteInvitation from './_partials/alert-dialogs/AlertDialogDeleteInvitation';
import { useTranslation } from 'react-i18next';

/**
 * Page de gestion des invitations pour une procédure
 */
export default function ProcedureInvitationsPage() {
    const { t } = useTranslation();
    const { procedureId } = useParams();
    const navigate = useNavigate();
    const { can } = usePermissions();

    // Sheet state
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    // AlertDialog states
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedInvitation, setSelectedInvitation] = useState(null);

    // Invitations state
    const [invitations, setInvitations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(null);

    // Récupération du template depuis l'API
    const {
        template,
        isLoading: isTemplateLoading,
        error: templateError,
        notFound,
    } = useProcedureTemplate(procedureId);

    // Charger les invitations
    const loadInvitations = useCallback(async () => {
        if (!template?.uniq_id) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await horodatyApi.listProcedureInvitations(template.uniq_id);
            const data = response.data?.data || response.data || [];
            setInvitations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Erreur chargement invitations:', err);
            setError('Impossible de charger les invitations');
        } finally {
            setIsLoading(false);
        }
    }, [template?.uniq_id]);

    useEffect(() => {
        loadInvitations();
    }, [loadInvitations]);

    // Handlers pour les actions sur les invitations
    const handleResendEmail = async (invitation) => {
        setActionInProgress(invitation.uniq_id);
        try {
            await horodatyApi.resendInvitationEmail(template.uniq_id, invitation.uniq_id);
            toast.success(t('procedures:emailRenvoyeAvecSucces'));
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || t('procedures:erreurLorsDeLEnvoiDeLEmail'));
        } finally {
            setActionInProgress(null);
        }
    };

    // Ouvrir le dialog de révocation
    const handleRevoke = (invitation) => {
        setSelectedInvitation(invitation);
        setRevokeDialogOpen(true);
    };

    // Confirmer la révocation
    const confirmRevoke = async () => {
        if (!selectedInvitation) return;

        setActionInProgress(selectedInvitation.uniq_id);
        try {
            await horodatyApi.revokeInvitation(template.uniq_id, selectedInvitation.uniq_id);
            toast.success(t('procedures:invitationRevoquee'));
            setRevokeDialogOpen(false);
            setSelectedInvitation(null);
            loadInvitations();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || t('procedures:erreurLorsDeLaRevocation'));
        } finally {
            setActionInProgress(null);
        }
    };

    const handleActivate = async (invitation) => {
        setActionInProgress(invitation.uniq_id);
        try {
            await horodatyApi.activateInvitation(template.uniq_id, invitation.uniq_id);
            toast.success(t('procedures:invitationReactivee'));
            loadInvitations();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || t('procedures:erreurLorsDeLaReactivation'));
        } finally {
            setActionInProgress(null);
        }
    };

    // Ouvrir le dialog de suppression
    const handleDelete = (invitation) => {
        setSelectedInvitation(invitation);
        setDeleteDialogOpen(true);
    };

    // Confirmer la suppression
    const confirmDelete = async () => {
        if (!selectedInvitation) return;

        setActionInProgress(selectedInvitation.uniq_id);
        try {
            await horodatyApi.deleteInvitation(template.uniq_id, selectedInvitation.uniq_id);
            toast.success(t('procedures:invitationSupprimee'));
            setDeleteDialogOpen(false);
            setSelectedInvitation(null);
            loadInvitations();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || t('procedures:erreurLorsDeLaSuppression'));
        } finally {
            setActionInProgress(null);
        }
    };

    // Colonnes de la DataTable
    const columns = useMemo(
        () =>
            createInvitationsColumns({
                onResendEmail: handleResendEmail,
                onRevoke: handleRevoke,
                onActivate: handleActivate,
                onDelete: handleDelete,
                actionInProgress,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [actionInProgress, template?.uniq_id, t],
    );

    // État de chargement initial
    if (!can('UPDATE_PROCEDURES')) {
        return (
            <div className="flex flex-col h-full">
                <PageHeader>
                    <div className="flex items-center gap-4">
                        <BackButton />
                        <div>
                            <div className="text-xs font-medium text-muted-foreground">{t('procedures:procedure')}</div>
                            <h1 className={'text-[20px] font-semibold text-foreground'}>{t('procedures:invitations')}</h1>
                        </div>
                    </div>
                </PageHeader>
                <div className="p-8 flex items-center justify-center min-h-[50vh]">
                    <span className="text-[14px] text-muted-foreground">{t('procedures:vousNAvezPasAccesACettePage')}</span>
                </div>
            </div>
        );
    }

    if (isTemplateLoading) {
        return (
            <div className="flex flex-col h-full">
                <PageHeader>
                    <div className="flex items-center gap-4">
                        <BackButton />
                        <div>
                            <div className="text-xs font-medium text-muted-foreground">{t('procedures:procedure')}</div>
                            <h1 className={'text-[20px] font-semibold text-foreground'}>{t('procedures:invitations')}</h1>
                        </div>
                    </div>
                </PageHeader>
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            </div>
        );
    }

    // Procédure non trouvée ou erreur
    if (notFound || templateError || !template) {
        return (
            <div className="flex flex-col h-full">
                <PageHeader>
                    <div className="flex items-center gap-4">
                        <BackButton />
                        <div>
                            <div className="text-xs font-medium text-muted-foreground">{t('procedures:procedure')}</div>
                            <h1 className={'text-[20px] font-semibold text-foreground'}>{t('procedures:invitations')}</h1>
                        </div>
                    </div>
                </PageHeader>
                <div className="flex flex-col h-full items-center justify-center">
                    <AlertCircle className="w-12 h-12 text-error mb-4" />
                    <p className="text-muted-foreground mb-4">{t('procedures:procedureNonTrouvee')}</p>
                    <Button variant="outline" onClick={() => navigate('/procedures')}>
                        {t('procedures:retourAuxProcedures')}
                    </Button>
                </div>
            </div>
        );
    }

    const hasValidConfiguration = template?.configuration?.sections?.some((section) => section.fields?.length > 0);

    return (
        <div className="flex flex-col h-full relative">
            {/* Header */}
            <PageHeader>
                <div className="flex items-center gap-4">
                    <BackButton />
                    <div>
                        <div className="text-xs font-medium text-muted-foreground">{template.name}</div>
                        <h1 className={'text-[20px] font-semibold text-foreground'}>{t('procedures:invitations')}</h1>
                    </div>
                </div>
            </PageHeader>

            {/* Content */}
            <div className="flex-1 overflow-y-auto scrollbar-hide overscroll-contain pb-[calc(80px+var(--safe-area-inset-bottom))] md:pb-0">
                <div className="px-[20px] py-[20px] space-y-[20px]">
                    {/* Action Bar */}
                    <div className="flex items-center justify-end gap-3">
                        <Button variant="solid" color="secondary" onClick={loadInvitations} disabled={isLoading}>
                            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                            {t('procedures:actualiser')}
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={() => setIsSheetOpen(true)}
                            disabled={!hasValidConfiguration}
                            title={!hasValidConfiguration ? t('procedures:ajoutezAuMoinsUnChampALaProcedure') : undefined}
                        >
                            <Plus className="h-4 w-4" />
                            {t('procedures:nouvelleInvitation')}
                        </Button>
                    </div>

                    {/* Error State */}
                    {error && (
                        <div className="text-center py-8">
                            <AlertCircle className="h-8 w-8 text-destructive/60 mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground mb-4">{error}</p>
                            <Button variant="outline" size="sm" onClick={loadInvitations}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                {t('procedures:reessayer')}
                            </Button>
                        </div>
                    )}

                    {/* DataTable */}
                    {!error && (
                        <div className="bg-card rounded-[12px] border border-border overflow-hidden">
                            <DataTable
                                columns={columns}
                                data={invitations}
                                isLoading={isLoading}
                                emptyMessage={t('procedures:aucuneInvitationEnvoyee')}
                                emptyIcon={<UserPlus className="h-12 w-12 text-border" />}
                                showPagination={invitations.length > 10}
                                pageSize={10}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Sheet pour créer des invitations */}
            <SheetInvitationCreate
                open={isSheetOpen}
                onClose={() => setIsSheetOpen(false)}
                procedureUniqId={template.uniq_id}
                onSuccess={loadInvitations}
            />

            {/* Dialogs de confirmation */}
            <AlertDialogRevokeInvitation
                open={revokeDialogOpen}
                onOpenChange={setRevokeDialogOpen}
                invitation={selectedInvitation}
                onConfirm={confirmRevoke}
                isLoading={!!actionInProgress}
            />
            <AlertDialogDeleteInvitation
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                invitation={selectedInvitation}
                onConfirm={confirmDelete}
                isLoading={!!actionInProgress}
            />
        </div>
    );
}
